import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { EntityManager } from './EntityManager';
import { Tower } from './Tower';
import { TreeSlot } from './TreeSlot';
import { InputRouter, PlayerId } from '../systems/InputRouter';
import { AudioRouter } from '../systems/AudioRouter';
import { COLORS, GAME, DEPTH } from '../constants';

const OCCLUSION_HORIZONTAL_RANGE = 90; // px - player is "behind" if within this X range of tower/tree

// ============================================
// Player Entity
// ============================================

export interface PlayerConfig extends Omit<EntityConfig, 'type'> {
  playerId: PlayerId;
  moveSpeed?: number;
}

export class Player extends Entity {
  public readonly playerId: PlayerId;
  private moveSpeed: number;
  private body: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image;
  private useImage: boolean = false;

  // Dancing state (frame-based, BPM-synced)
  private dancing: boolean = false;
  private baseDanceY: number = 0;
  private danceKeys: string[] = [];
  private danceFrameIndex: number = 0;
  private danceTimer: number = 0;
  private danceCycleCount: number = 0;       // How many 4-frame cycles completed
  private danceFlipped: boolean = false;     // Whether currently flipped for dance
  private lastDanceBPM: number = 0;          // For detecting BPM change and re-sync
  private danceLogTimer: number = 0;         // Throttle console log
  private readonly DANCE_FRAMES_PER_CYCLE = 4;
  private readonly DANCE_CYCLES_BEFORE_FLIP = 4;  // 4 cycles of 4 frames, then flip

  // Upgrade dancing animation (when standing on tower during passive upgrade)
  private upgradeDancing: boolean = false;
  private upgradeDanceTimer: number = 0;

  // Direction tracking for sprite flipping
  private facingRight: boolean = true;

  // Animation state
  private isMoving: boolean = false;
  private runFrameIndex: number = 0;
  private animationTimer: number = 0;
  private readonly ANIMATION_INTERVAL: number = 75; // ms between frames (4 run frames, half of previous 2-frame timing)
  private standingKey: string = '';
  private runKeys: string[] = [];

  // Shadow (no ghost - stays with main sprite only)
  private shadow: Phaser.GameObjects.Container;

  // Ghost sprite for see-through visibility (2.5D)
  private ghostSprite: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle | null = null;

  // Invincibility blinking (after being hit by monster)
  private invincible: boolean = false;
  private blinkTimer: number = 0;
  private readonly BLINK_INTERVAL: number = 100; // ms between blinks

  // Hit reaction: small jump (bounce) and red flash
  private hitBounceOffsetY: number = 0;
  private bodyColor: number = 0; // For rectangle fallback when restoring after red flash

  // Acceleration/deceleration (pixels per second; applied to all players)
  private velX: number = 0;
  private velY: number = 0;
  private readonly ACCELERATION: number = 1 / 0.15;  // Reach top speed in ~0.15s
  private readonly DECELERATION: number = 1250;     // px/s² — stop in ~0.2s from full speed

  constructor(scene: Phaser.Scene, config: PlayerConfig) {
    super(scene, { ...config, type: 'player' });

    this.playerId = config.playerId;
    this.moveSpeed = config.moveSpeed ?? GAME.PLAYER_SPEED;

    // Set up image keys based on player (P3/P4 fall back to colored rect if sprites missing)
    const playerNum = this.playerId.replace('P', '');
    this.standingKey = `player-${playerNum}-standing`;
    this.runKeys = [
      `player-${playerNum}-run-1`,
      `player-${playerNum}-run-2`,
      `player-${playerNum}-run-3`,
      `player-${playerNum}-run-4`,
    ];
    this.danceKeys = [
      `player-${playerNum}-dance-1`,
      `player-${playerNum}-dance-2`,
      `player-${playerNum}-dance-3`,
      `player-${playerNum}-dance-4`,
    ];

    const playerColors: Record<PlayerId, number> = {
      P1: COLORS.PLAYER_1,
      P2: COLORS.PLAYER_2,
      P3: COLORS.PLAYER_3,
      P4: COLORS.PLAYER_4,
    };
    const color = playerColors[this.playerId];

    if (scene.textures.exists(this.standingKey)) {
      this.useImage = true;
      const img = scene.add.image(0, 0, this.standingKey);
      img.setScale(0.21);
      this.body = img;
    } else {
      // Fallback to colored rectangle
      const rect = scene.add.rectangle(0, 0, GAME.PLAYER_WIDTH, GAME.PLAYER_HEIGHT, color);
      rect.setStrokeStyle(2, 0xffffff);
      this.body = rect;
      this.bodyColor = color;
    }

    // Shadow - fuzzy ellipses at bottom of player (no ghost, stays with main sprite only)
    const bodyBottomY = this.body instanceof Phaser.GameObjects.Image
      ? (this.body as Phaser.GameObjects.Image).displayHeight / 2
      : GAME.PLAYER_HEIGHT / 2;
    this.shadow = scene.add.container(0, bodyBottomY - 10);
    const shadowLayers = [
      { width: 64, height: 20, alpha: 0.06 },
      { width: 58, height: 18, alpha: 0.08 },
      { width: 52, height: 16, alpha: 0.1 },
      { width: 46, height: 14, alpha: 0.12 },
      { width: 40, height: 13, alpha: 0.14 },
      { width: 32, height: 10, alpha: 0.18 },
    ];
    shadowLayers.forEach(layer => {
      const ellipse = scene.add.ellipse(0, 0, layer.width, layer.height, 0x000000, layer.alpha);
      this.shadow.add(ellipse);
    });

    this.container.add([this.shadow, this.body]);
    this.updateDepth();

    // Create ghost sprite for see-through visibility when behind objects
    this.createGhostSprite();
  }

  /**
   * Create a semi-transparent ghost sprite on a high layer for visibility
   */
  private createGhostSprite(): void {
    const playerColors: Record<PlayerId, number> = {
      P1: COLORS.PLAYER_1,
      P2: COLORS.PLAYER_2,
      P3: COLORS.PLAYER_3,
      P4: COLORS.PLAYER_4,
    };
    const color = playerColors[this.playerId];

    if (this.useImage && this.scene.textures.exists(this.standingKey)) {
      this.ghostSprite = this.scene.add.image(this._x, this._y, this.standingKey);
      (this.ghostSprite as Phaser.GameObjects.Image).setScale(0.21);
      (this.ghostSprite as Phaser.GameObjects.Image).setAlpha(0.4);
    } else {
      this.ghostSprite = this.scene.add.rectangle(this._x, this._y, GAME.PLAYER_WIDTH, GAME.PLAYER_HEIGHT, color);
      (this.ghostSprite as Phaser.GameObjects.Rectangle).setAlpha(0.4);
    }
    this.ghostSprite.setDepth(DEPTH.GHOST_LAYER);
  }

  /** Bottom of player visual for depth sorting (lower on screen = in front). Uses container.y + body height/2 because GetBottom(body) returns local coords for container children. */
  protected getDepthY(): number {
    const h = (this.body as Phaser.GameObjects.Components.ComputedSize).displayHeight ?? GAME.PLAYER_HEIGHT;
    return this.container.y + h / 2;
  }

  update(delta: number): void {
    // Update invincibility blinking effect
    if (this.invincible) {
      this.updateBlinking(delta);
    }

    // Upgrade dancing: visual only, still allow movement (so player can walk off tower)
    if (this.upgradeDancing) {
      this.updateUpgradeDance(delta);
      this.syncGhostSprite();
      // Fall through so movement is still processed
    }

    // Tower dance (press D on tower): locked in place until D pressed again
    if (this.dancing) {
      this.updateDance(delta);
      this.syncGhostSprite();
      this.updateDepth();
      return;
    }

    // Get movement input from InputRouter
    const movement = InputRouter.getMovementVector(this.playerId);
    const wasMoving = this.isMoving;
    this.isMoving = movement.x !== 0 || movement.y !== 0;

    const dt = delta / 1000;

    if (this.isMoving) {
      // Target velocity (normalized direction × top speed)
      const targetVelX = movement.x * this.moveSpeed;
      const targetVelY = movement.y * this.moveSpeed;
      // Accelerate toward target
      const accel = Math.min(1, this.ACCELERATION * dt);
      this.velX += (targetVelX - this.velX) * accel;
      this.velY += (targetVelY - this.velY) * accel;
    } else {
      // Decelerate (slide to a stop)
      const decelAmount = this.DECELERATION * dt;
      const speed = Math.sqrt(this.velX * this.velX + this.velY * this.velY);
      if (speed > 0) {
        const scale = Math.max(0, 1 - decelAmount / speed);
        this.velX *= scale;
        this.velY *= scale;
      }
    }

    // Apply velocity to position
    this.x += this.velX * dt;
    this.y += this.velY * dt;

    if (this.isMoving) {
      // Update facing direction for sprite flipping
      if (this.useImage && movement.x !== 0) {
        const shouldFaceRight = movement.x > 0;
        if (shouldFaceRight !== this.facingRight) {
          this.facingRight = shouldFaceRight;
          (this.body as Phaser.GameObjects.Image).setFlipX(!this.facingRight);
        }
      }

      // Handle running animation
      if (this.useImage && this.runKeys.length > 0 && this.scene.textures.exists(this.runKeys[0])) {
        this.animationTimer += delta;
        if (this.animationTimer >= this.ANIMATION_INTERVAL) {
          this.animationTimer = 0;
          this.runFrameIndex = (this.runFrameIndex + 1) % this.runKeys.length;
          (this.body as Phaser.GameObjects.Image).setTexture(this.runKeys[this.runFrameIndex]);
        }
      }
    } else if (wasMoving && !this.isMoving) {
      // Just stopped moving - switch back to standing image
      if (this.useImage && this.scene.textures.exists(this.standingKey)) {
        (this.body as Phaser.GameObjects.Image).setTexture(this.standingKey);
        this.animationTimer = 0;
        this.runFrameIndex = 0;
      }
    }

    // Re-apply upgrade dance bounce or hit bounce (container follows logical position + offset)
    this.container.x = this._x;
    if (this.upgradeDancing) {
      this.container.y = Math.round(this._y + this.getDanceBounceOffset());
    } else {
      this.container.y = this._y + this.hitBounceOffsetY;
    }

    // Depth updated before ghost sync so isBehindTowerOrTree uses current depth
    this.updateDepth();

    // Sync ghost sprite (show only when occluded by tower/tree)
    this.syncGhostSprite();
  }

  /**
   * Update blinking effect during invincibility
   */
  private updateBlinking(delta: number): void {
    this.blinkTimer += delta;
    
    // Toggle visibility every BLINK_INTERVAL
    const blinkPhase = Math.floor(this.blinkTimer / this.BLINK_INTERVAL) % 2;
    const alpha = blinkPhase === 0 ? 1.0 : 0.3;
    
    this.container.setAlpha(alpha);
    if (this.ghostSprite) {
      this.ghostSprite.setAlpha(alpha * 0.4); // Ghost is already 40% alpha
    }
  }

  /** True if player is behind any tower or tree (would be occluded). Uses actual container depth since that's what Phaser uses for render order. */
  private isBehindTowerOrTree(): boolean {
    const playerDepth = this.container.depth;
    const px = this.container.x;

    const towers = EntityManager.getByType<Tower>('tower');
    for (const t of towers) {
      if (!t.active || t.destroyed) continue;
      if (Math.abs(t.x - px) > OCCLUSION_HORIZONTAL_RANGE) continue;
      if (t.getContainer().depth > playerDepth) return true; // tower renders in front of player
    }

    const slots = EntityManager.getByType<TreeSlot>('treeSlot');
    for (const s of slots) {
      if (!s.active || s.destroyed || s.isOccupied()) continue; // only trees (unoccupied)
      if (Math.abs(s.x - px) > OCCLUSION_HORIZONTAL_RANGE) continue;
      if (s.getContainer().depth > playerDepth) return true;
    }
    return false;
  }

  /**
   * Sync ghost sprite with main sprite. Show ghost only when behind a tower or tree (occluded).
   */
  private syncGhostSprite(): void {
    if (!this.ghostSprite) return;

    const showGhost = this.isBehindTowerOrTree();
    this.ghostSprite.setVisible(showGhost);
    if (!showGhost) return;

    this.ghostSprite.setPosition(this.container.x, this.container.y);

    // Sync texture and flip for image sprites
    if (this.useImage && this.ghostSprite instanceof Phaser.GameObjects.Image) {
      const mainImg = this.body as Phaser.GameObjects.Image;
      this.ghostSprite.setTexture(mainImg.texture.key);
      this.ghostSprite.setFlipX(mainImg.flipX);
      this.ghostSprite.setRotation(mainImg.rotation);
      this.ghostSprite.setScale(mainImg.scaleX, mainImg.scaleY);
    }
  }

  /**
   * Whether dance frame textures are available (P1/P2 have them; P3/P4 may not yet)
   */
  private hasDanceFrames(): boolean {
    return this.useImage && this.danceKeys.length > 0 && this.scene.textures.exists(this.danceKeys[0]);
  }

  /**
   * Advance BPM-synced dance frame animation and update body texture/flip.
   * Used by both tower dance and upgrade dance. Pattern: 4 frames x 4 cycles, then flip, repeat.
   */
  private updateDanceFrameAnimation(delta: number): void {
    if (!this.hasDanceFrames()) return;

    const bpm = AudioRouter.getCurrentBPM();
    const trackKey = AudioRouter.getCurrentTrackKey() || '(unknown)';

    if (bpm <= 0 || !Number.isFinite(bpm)) {
      console.error(`[Player ${this.playerId}] Cannot dance to beat: BPM is invalid (${bpm}). Expected > 0 and finite.`);
      return;
    }

    if (this.lastDanceBPM !== bpm) {
      this.lastDanceBPM = bpm;
      this.danceTimer = 0;
      this.danceFrameIndex = 0;
      this.danceLogTimer = 0;
      console.log(`[Player ${this.playerId}] Dancing to beat: ${bpm} BPM (track: ${trackKey})`);
    }

    this.danceLogTimer += delta;
    if (this.danceLogTimer >= 2000 && this.playerId === 'P1') {
      this.danceLogTimer = 0;
      console.log(`[Player] Dancing to beat: ${bpm} BPM (track: ${trackKey})`);
    }

    const beatMs = 60000 / bpm;
    const frameMs = beatMs / this.DANCE_FRAMES_PER_CYCLE;

    this.danceTimer += delta;

    if (this.danceTimer >= frameMs) {
      this.danceTimer -= frameMs;
      this.danceFrameIndex++;

      if (this.danceFrameIndex >= this.DANCE_FRAMES_PER_CYCLE) {
        this.danceFrameIndex = 0;
        this.danceCycleCount++;
        if (this.danceCycleCount >= this.DANCE_CYCLES_BEFORE_FLIP) {
          this.danceCycleCount = 0;
          this.danceFlipped = !this.danceFlipped;
          (this.body as Phaser.GameObjects.Image).setFlipX(this.danceFlipped);
        }
      }

      const frameKey = this.danceKeys[this.danceFrameIndex];
      if (this.scene.textures.exists(frameKey)) {
        (this.body as Phaser.GameObjects.Image).setTexture(frameKey);
      }
    }
  }

  /**
   * Update frame-based dance animation when dancing on tower (press D)
   */
  private updateDance(delta: number): void {
    if (!this.useImage) return;

    if (this.hasDanceFrames()) {
      this.updateDanceFrameAnimation(delta);
      const bounce = this.getDanceBounceOffset();
      this.container.y = Math.round(this.baseDanceY + bounce);
    } else {
      const bounce = Math.sin(Date.now() / 100) * 3;
      this.container.y = Math.round(this.baseDanceY + bounce);
    }
  }

  /** Current bounce offset for dance (so we can re-apply after movement during upgrade dance) */
  private getDanceBounceOffset(): number {
    if (this.hasDanceFrames()) {
      const bouncePhase = (this.danceFrameIndex / this.DANCE_FRAMES_PER_CYCLE) * Math.PI * 2;
      return Math.sin(bouncePhase) * 2;
    }
    return 0;
  }

  /**
   * Update when standing on tower during upgrade (same frame-based dance as tower dance)
   */
  private updateUpgradeDance(delta: number): void {
    if (!this.useImage) return;

    if (this.hasDanceFrames()) {
      this.updateDanceFrameAnimation(delta);
      const bounce = this.getDanceBounceOffset();
      this.container.y = Math.round(this._y + bounce);
    } else {
      // Fallback: simple tilt if no dance frames
      this.upgradeDanceTimer += delta;
      const tiltAmount = Math.sin(this.upgradeDanceTimer / 200) * 0.15;
      const flipPhase = Math.floor(this.upgradeDanceTimer / 400) % 2;
      (this.body as Phaser.GameObjects.Image).setRotation(tiltAmount);
      (this.body as Phaser.GameObjects.Image).setFlipX(flipPhase === 1);
    }
  }

  /**
   * Constrain player to map bounds
   */
  constrainToBounds(minX: number, minY: number, maxX: number, maxY: number): void {
    if (this.dancing) return; // Don't constrain while dancing

    this.x = Phaser.Math.Clamp(this._x, minX, maxX);
    this.y = Phaser.Math.Clamp(this._y, minY, maxY);
  }

  /**
   * Check if player can interact with a position
   */
  isInRangeOf(targetX: number, targetY: number, range: number = 60): boolean {
    return this.distanceTo({ x: targetX, y: targetY }) <= range;
  }

  /**
   * Get the tower type selected by this player
   */
  getSelectedTowerType(): string {
    // This would be managed by game state in a full implementation
    return 'arrow';
  }

  setMoveSpeed(speed: number): void {
    this.moveSpeed = speed;
  }

  // ============================================
  // Dancing Methods
  // ============================================

  /**
   * Reset dance frame state and show first dance frame (used by both tower dance and upgrade dance)
   */
  private resetDanceAnimationState(): void {
    this.danceFrameIndex = 0;
    this.danceTimer = 0;
    this.danceCycleCount = 0;
    this.danceFlipped = false;
    if (this.useImage && this.danceKeys.length > 0 && this.scene.textures.exists(this.danceKeys[0])) {
      (this.body as Phaser.GameObjects.Image).setTexture(this.danceKeys[0]);
      (this.body as Phaser.GameObjects.Image).setFlipX(false);
      (this.body as Phaser.GameObjects.Image).clearTint();
    }
  }

  /**
   * Set dancing state (frame-based animation synced to BPM)
   */
  setDancing(dancing: boolean): void {
    this.dancing = dancing;
    if (dancing) {
      this.baseDanceY = this._y;
      this.resetDanceAnimationState();
    } else {
      // Restore Y position
      this.y = this.baseDanceY;
      this.container.y = this._y;
      // Restore to standing texture
      if (this.useImage && this.scene.textures.exists(this.standingKey)) {
        (this.body as Phaser.GameObjects.Image).setTexture(this.standingKey);
        (this.body as Phaser.GameObjects.Image).setFlipX(!this.facingRight);
      }
    }
  }

  /**
   * Check if player is dancing
   */
  isDancing(): boolean {
    return this.dancing;
  }

  /**
   * Get player ID
   */
  getPlayerId(): PlayerId {
    return this.playerId;
  }

  /**
   * Set bounce offset - kept for API compatibility but no longer used
   * (Player now handles its own BPM-synced dance animation internally)
   */
  setBounceOffset(_offset: number): void {
    // No-op: dance animation is now self-contained in updateUpgradeDance()
  }

  /**
   * Set upgrade dancing state (same frame-based dance as tower dance when frames exist)
   */
  setUpgradeDancing(dancing: boolean): void {
    if (dancing === this.upgradeDancing) return;

    this.upgradeDancing = dancing;

    if (dancing) {
      this.upgradeDanceTimer = 0;
      this.resetDanceAnimationState();
    } else {
      this.upgradeDanceTimer = 0;
      // Reset position (remove any bounce offset)
      this.container.y = this._y;
      if (this.useImage) {
        const img = this.body as Phaser.GameObjects.Image;
        img.setRotation(0);
        img.setFlipX(!this.facingRight);
        img.clearTint();
        if (this.scene.textures.exists(this.standingKey)) {
          img.setTexture(this.standingKey);
        }
      } else {
        (this.body as Phaser.GameObjects.Rectangle).setRotation(0);
      }
    }
  }

  /**
   * Check if player is upgrade dancing
   */
  isUpgradeDancing(): boolean {
    return this.upgradeDancing;
  }

  /**
   * Play small jump/bounce when hit by monster
   */
  playHitBounce(): void {
    if (this.destroyed || !this.scene) return;
    const offset = { y: 0 };
    this.scene.tweens.add({
      targets: offset,
      y: -22,
      duration: 100,
      yoyo: true,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        if (this.destroyed) return;
        this.hitBounceOffsetY = offset.y;
      },
    });
  }

  /**
   * Flash player red for a moment when hit by monster
   */
  flashRed(durationMs: number = 1000): void {
    if (this.destroyed || !this.scene) return;
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      (this.body as Phaser.GameObjects.Image).setTint(0xff0000);
      this.scene.time.delayedCall(durationMs, () => {
        if (!this.destroyed && this.body instanceof Phaser.GameObjects.Image) {
          (this.body as Phaser.GameObjects.Image).clearTint();
        }
      });
    } else if (this.body instanceof Phaser.GameObjects.Rectangle) {
      (this.body as Phaser.GameObjects.Rectangle).setFillStyle(0xff0000);
      this.scene.time.delayedCall(durationMs, () => {
        if (!this.destroyed && this.body instanceof Phaser.GameObjects.Rectangle) {
          (this.body as Phaser.GameObjects.Rectangle).setFillStyle(this.bodyColor);
        }
      });
    }
  }

  /**
   * Start invincibility blinking (after being hit by monster)
   */
  setInvincible(invincible: boolean): void {
    this.invincible = invincible;
    
    if (!invincible) {
      // Reset appearance when invincibility ends
      this.blinkTimer = 0;
      this.container.setAlpha(1.0);
      if (this.ghostSprite) {
        this.ghostSprite.setAlpha(0.4);
      }
    }
  }

  /**
   * Check if player is invincible
   */
  isInvincible(): boolean {
    return this.invincible;
  }

  /**
   * Clean up ghost sprite on destroy
   */
  destroy(): void {
    if (this.ghostSprite) {
      this.ghostSprite.destroy();
      this.ghostSprite = null;
    }
    super.destroy();
  }
}
